import React, { useEffect, useMemo, useState } from 'react';
import { addDoc, collection, deleteDoc, doc, onSnapshot, orderBy, query, updateDoc } from 'firebase/firestore';
import RoomGrid, { itemColors } from './RoomGrid';

const MAX_DIMENSION = 25;

function SavedRoomCard({ room, onDelete, onEdit, isDeleting, isEditing }) {
  const width = Number(room.width) || 0;
  const length = Number(room.length) || 0;

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: '10px', padding: '15px', background:'var(--light)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'flex-start', marginBottom: '12px' }}>
        <div>
          <div style={{ fontWeight: 'bold', fontSize: '15px' }}>{room.name}</div>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>
            {width} x {length} squares
          </div>
          {room.managerName && (
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>
              Manager: {room.managerName}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-end' }}>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
            {room.createdAt ? new Date(room.createdAt.seconds ? room.createdAt.seconds * 1000 : room.createdAt).toLocaleString() : 'Just now'}
          </div>
          <button
            type="button"
            onClick={() => onEdit(room)}
            style={{ background: isEditing ? 'var(--primary)' : 'transparent', color: isEditing ? 'white' : 'var(--primary)', border: '1px solid var(--primary)', fontSize: '11px', padding: '5px 8px' }}
          >
            {isEditing ? 'Editing' : 'Edit Room'}
          </button>
          <button
            type="button"
            onClick={() => onDelete(room.id)}
            disabled={isDeleting}
            style={{ background: 'transparent', color: 'var(--danger)', border: '1px solid var(--danger)', fontSize: '11px', padding: '5px 8px' }}
          >
            {isDeleting ? 'Deleting...' : 'Delete Room'}
          </button>
        </div>
      </div>
      <RoomGrid width={width} length={length} items={room.items || []} />
      {room.items && room.items.length > 0 && (
        <div style={{ marginTop: '12px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {room.items.map((item, index) => (
            <span
              key={`${room.id}-${item.label}-${index}`}
              style={{
                fontSize: '11px',
                padding: '4px 8px',
                borderRadius: '999px',
                background: itemColors[index % itemColors.length],
                color: '#0f172a'
              }}
            >
              {item.label} ({(item.cells || []).length})
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function CreateRoom({ db, usersList = [] }) {
  const [roomName, setRoomName] = useState('');
  const [width, setWidth] = useState('');
  const [length, setLength] = useState('');
  const [managerInitials, setManagerInitials] = useState('');
  const [editingRoomId, setEditingRoomId] = useState('');
  const [rooms, setRooms] = useState([]);
  const [saveMessage, setSaveMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [itemLabel, setItemLabel] = useState('');
  const [draftItems, setDraftItems] = useState([]);
  const [selectedCells, setSelectedCells] = useState([]);
  const [isDraggingSelection, setIsDraggingSelection] = useState(false);
  const [dragMode, setDragMode] = useState('add');
  const [deletingRoomId, setDeletingRoomId] = useState('');

  useEffect(() => {
    const roomsQuery = query(collection(db, 'rooms'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(roomsQuery, (snapshot) => {
      setRooms(snapshot.docs.map((roomDoc) => ({ id: roomDoc.id, ...roomDoc.data() })));
    });

    return () => unsubscribe();
  }, [db]);

  useEffect(() => {
    if (!isDraggingSelection) return undefined;

    const handleMouseUp = () => {
      setIsDraggingSelection(false);
    };

    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDraggingSelection]);

  const parsedWidth = useMemo(() => Number(width) || 0, [width]);
  const parsedLength = useMemo(() => Number(length) || 0, [length]);
  const availableManagers = useMemo(
    () => usersList
      .filter((user) => ['Manager', 'Planner', 'Admin', 'Super Admin'].includes(user.role))
      .sort((a, b) => String(a.displayName || a.initials || '').localeCompare(String(b.displayName || b.initials || ''), undefined, { sensitivity: 'base' })),
    [usersList]
  );
  const isSuccessMessage = ['Room saved.', 'Room updated.', 'Item added to room.', 'Room deleted.', 'Item removed from draft room.'].includes(saveMessage);

  const resetForm = () => {
    setRoomName('');
    setWidth('');
    setLength('');
    setManagerInitials('');
    setEditingRoomId('');
    setDraftItems([]);
    setSelectedCells([]);
    setItemLabel('');
  };

  const handleCellClick = (cellKey) => {
    setSelectedCells((previous) => (
      previous.includes(cellKey)
        ? previous.filter((existingKey) => existingKey !== cellKey)
        : [...previous, cellKey]
    ));
  };

  const applyDragSelection = (cellKey, mode) => {
    setSelectedCells((previous) => {
      if (mode === 'remove') {
        return previous.includes(cellKey)
          ? previous.filter((existingKey) => existingKey !== cellKey)
          : previous;
      }

      return previous.includes(cellKey)
        ? previous
        : [...previous, cellKey];
    });
  };

  const handleSelectionStart = (cellKey) => {
    const nextMode = selectedCells.includes(cellKey) ? 'remove' : 'add';
    setDragMode(nextMode);
    setIsDraggingSelection(true);
    applyDragSelection(cellKey, nextMode);
  };

  const handleSelectionEnter = (cellKey) => {
    if (!isDraggingSelection) return;
    applyDragSelection(cellKey, dragMode);
  };

  const handleSelectionEnd = () => {
    setIsDraggingSelection(false);
  };

  const handleAddItem = () => {
    if (!itemLabel.trim()) {
      setSaveMessage('Enter an item label first.');
      return;
    }

    if (!selectedCells.length) {
      setSaveMessage('Select one or more squares first.');
      return;
    }

    const selectedLookup = selectedCells.reduce((accumulator, cellKey) => {
      accumulator[cellKey] = true;
      return accumulator;
    }, {});

    const filteredItems = draftItems
      .map((item) => ({
        ...item,
        cells: (item.cells || []).filter((cellKey) => !selectedLookup[cellKey])
      }))
      .filter((item) => item.cells.length > 0);

    setDraftItems([
      ...filteredItems,
      {
        label: itemLabel.trim(),
        cells: [...selectedCells].sort()
      }
    ]);
    setItemLabel('');
    setSelectedCells([]);
    setSaveMessage('Item added to room.');
  };

  const handleClearSelection = () => {
    setSelectedCells([]);
    setIsDraggingSelection(false);
  };

  const handleDeleteDraftItem = (indexToDelete) => {
    setDraftItems((previous) => previous.filter((_, index) => index !== indexToDelete));
    setSaveMessage('Item removed from draft room.');
  };

  const handleEditRoom = (room) => {
    setRoomName(room.name || '');
    setWidth(String(room.width || ''));
    setLength(String(room.length || ''));
    setManagerInitials(room.managerInitials || '');
    setEditingRoomId(room.id);
    setDraftItems(room.items || []);
    setSelectedCells([]);
    setIsDraggingSelection(false);
    setItemLabel('');
    setSaveMessage(`Editing ${room.name || 'room'}.`);
  };

  const handleCancelEdit = () => {
    resetForm();
    setSaveMessage('Edit cancelled.');
  };

  const handleSaveRoom = async (event) => {
    event.preventDefault();

    if (!parsedWidth || !parsedLength) {
      setSaveMessage('Enter valid room dimensions first.');
      return;
    }

    if (parsedWidth > MAX_DIMENSION || parsedLength > MAX_DIMENSION) {
      setSaveMessage(`Room dimensions must be ${MAX_DIMENSION} squares or less.`);
      return;
    }

    setIsSaving(true);

    try {
      const selectedManager = availableManagers.find((manager) => manager.initials === managerInitials);
      const sanitizedItems = draftItems
        .map((item) => ({
          ...item,
          cells: (item.cells || []).filter((cellKey) => {
            const [row, column] = cellKey.split('-').map(Number);
            return row >= 1 && row <= parsedLength && column >= 1 && column <= parsedWidth;
          })
        }))
        .filter((item) => item.cells.length > 0);

      if (editingRoomId) {
        await updateDoc(doc(db, 'rooms', editingRoomId), {
          name: roomName.trim() || `Room ${rooms.length + 1}`,
          width: parsedWidth,
          length: parsedLength,
          managerInitials: selectedManager?.initials || '',
          managerName: selectedManager?.displayName || '',
          items: sanitizedItems,
          updatedAt: new Date()
        });
        setSaveMessage('Room updated.');
      } else {
        await addDoc(collection(db, 'rooms'), {
          name: roomName.trim() || `Room ${rooms.length + 1}`,
          width: parsedWidth,
          length: parsedLength,
          managerInitials: selectedManager?.initials || '',
          managerName: selectedManager?.displayName || '',
          items: sanitizedItems,
          createdAt: new Date()
        });
        setSaveMessage('Room saved.');
      }

      resetForm();
    } catch (error) {
      console.error('Error saving room:', error);
      setSaveMessage(editingRoomId ? 'Failed to update room.' : 'Failed to save room.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteRoom = async (roomId) => {
    setDeletingRoomId(roomId);

    try {
      await deleteDoc(doc(db, 'rooms', roomId));
      setSaveMessage('Room deleted.');
    } catch (error) {
      console.error('Error deleting room:', error);
      setSaveMessage('Failed to delete room.');
    } finally {
      setDeletingRoomId('');
    }
  };

  return (
    <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginBottom: '25px' }}>
        <button className="active-btn">Create Room</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(320px, 420px) minmax(320px, 1fr)', gap: '20px', alignItems: 'start' }}>
        <div className="card" style={{ margin: 0 }}>
          <h3 style={{ marginTop: 0 }}>{editingRoomId ? 'Edit Room' : 'Create Room'}</h3>
          <p className="text-sm" style={{ marginBottom: '20px' }}>
            {editingRoomId ? 'Update the saved room layout and save your changes.' : 'Enter the room size in square units to generate and save a room layout.'}
          </p>

          <form onSubmit={handleSaveRoom} style={{ display: 'grid', gap: '14px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '6px' }}>Room Name</label>
              <input
                value={roomName}
                onChange={(event) => setRoomName(event.target.value)}
                placeholder="e.g. Interview Room"
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '6px' }}>Width</label>
              <input
                type="number"
                min="1"
                max={MAX_DIMENSION}
                value={width}
                onChange={(event) => setWidth(event.target.value)}
                placeholder="e.g. 6"
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '6px' }}>Length</label>
              <input
                type="number"
                min="1"
                max={MAX_DIMENSION}
                value={length}
                onChange={(event) => setLength(event.target.value)}
                placeholder="e.g. 8"
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '6px' }}>Manager Responsible</label>
              <select value={managerInitials} onChange={(event) => setManagerInitials(event.target.value)}>
                <option value="">Select a manager</option>
                {availableManagers.map((manager) => (
                  <option key={manager.initials} value={manager.initials}>
                    {manager.displayName} ({manager.initials})
                  </option>
                ))}
              </select>
            </div>

            {saveMessage && (
              <div style={{ fontSize: '12px', fontWeight: 'bold', color: isSuccessMessage ? 'var(--success)' : 'var(--danger)' }}>
                {saveMessage}
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <button type="submit" disabled={isSaving} style={{ background: 'var(--primary)', color: 'white' }}>
                {isSaving ? (editingRoomId ? 'Updating...' : 'Saving...') : (editingRoomId ? 'Update Room' : 'Save Room')}
              </button>
              {editingRoomId && (
                <button type="button" onClick={handleCancelEdit} style={{ background: 'var(--light)', color: 'var(--text-main)' }}>
                  Cancel Edit
                </button>
              )}
            </div>
          </form>
        </div>

        <div className="card" style={{ margin: 0 }}>
          <h3 style={{ marginTop: 0 }}>Generated Room</h3>
          <RoomGrid
            width={parsedWidth}
            length={parsedLength}
            items={draftItems}
            selectedCells={selectedCells}
            onCellClick={handleCellClick}
            onCellMouseDown={handleSelectionStart}
            onCellMouseEnter={handleSelectionEnter}
            onGridMouseUp={handleSelectionEnd}
          />
          <div style={{ display: 'flex', gap: '10px', marginTop: '15px', alignItems: 'end', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 220px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '6px' }}>Item Label</label>
              <input
                value={itemLabel}
                onChange={(event) => setItemLabel(event.target.value)}
                placeholder="e.g. Desk"
              />
            </div>
            <button type="button" onClick={handleAddItem} style={{ background: 'var(--primary)', color: 'white' }}>
              Add Item
            </button>
            <button type="button" onClick={handleClearSelection} style={{ background: 'var(--light)', color: 'var(--text-main)' }}>
              Clear Selection
            </button>
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '10px' }}>
            Click and drag across squares to paint an item shape, then add a label such as Desk or Table.
          </div>
          {draftItems.length > 0 && (
            <div style={{ marginTop: '16px', display: 'grid', gap: '8px' }}>
              {draftItems.map((item, index) => (
                <div key={`${item.label}-${index}`} style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', alignItems: 'center', background:'var(--light)', borderRadius: '8px', padding: '8px 10px' }}>
                  <div style={{ fontSize: '12px' }}>
                    <strong>{item.label}</strong> on {(item.cells || []).length} squares
                  </div>
                  <button type="button" onClick={() => handleDeleteDraftItem(index)} style={{ background: 'transparent', color: 'var(--danger)', border: '1px solid var(--danger)', fontSize: '11px', padding: '4px 8px' }}>
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="card" style={{ marginTop: '20px' }}>
        <div className="flex-between" style={{ marginBottom: '15px' }}>
          <h3 style={{ margin: 0 }}>Saved Rooms</h3>
          <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{rooms.length} saved</span>
        </div>

        {rooms.length === 0 ? (
          <p className="text-sm" style={{ margin: 0 }}>No rooms saved yet.</p>
        ) : (
          <div style={{ display: 'grid', gap: '14px' }}>
            {rooms.map((room) => (
              <SavedRoomCard
                key={room.id}
                room={room}
                onDelete={handleDeleteRoom}
                onEdit={handleEditRoom}
                isDeleting={deletingRoomId === room.id}
                isEditing={editingRoomId === room.id}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default CreateRoom;